import React, { useState, useEffect } from 'react';
import { Star, Trophy, MapPin, ChevronRight, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Gold / silver / bronze badge style — anything past 3rd is muted
const RANK_BADGE_CLASSES = [
  'bg-[#e8ff00] text-black',
  'bg-zinc-600 text-white',
  'bg-[#b87333] text-white',
];
const RANK_BADGE_FALLBACK = 'bg-zinc-800 text-zinc-500 border border-zinc-700';

const TopRatedGyms = ({ onGymClick }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopRated = async () => {
      try {
        const [statsRes, recentRes] = await Promise.all([
          fetch(`${API_URL}/api/gym-reviews/stats`),
          fetch(`${API_URL}/api/gym-reviews/recent?limit=20`),
        ]);
        const stats = statsRes.ok ? await statsRes.json() : {};
        const recent = recentRes.ok ? await recentRes.json() : [];

        // Build top rated list from stats
        const topGyms = Object.entries(stats)
          .map(([key, data]) => {
            const [name, city, state] = key.split('|');
            const gymReviews = recent.filter(r => r.gym_key === key);
            const latestReview = gymReviews[0] || null;
            return { name, city, state, avg: data.avg, count: data.count, latestReview };
          })
          .sort((a, b) => {
            // Sort by rating first, then by review count
            if (b.avg !== a.avg) return b.avg - a.avg;
            return b.count - a.count;
          })
          .slice(0, 10);

        setReviews(topGyms);
      } catch {
        // silent
      }
      setLoading(false);
    };
    fetchTopRated();
  }, []);

  if (loading || reviews.length === 0) return null;

  return (
    <div className="mb-8 bg-zinc-900/60 border border-zinc-800 p-4 sm:p-6" data-testid="top-rated-gyms">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-2 bg-[#e8ff00] px-3 py-1.5">
          <TrendingUp className="w-3.5 h-3.5 text-black" />
          <span className="text-black text-[11px] font-heading uppercase tracking-wider">Top Rated</span>
        </div>
        <h3 className="text-white font-heading text-base">Community Favorites</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="top-rated-grid">
        {reviews.map((gym, i) => (
          <button
            key={`${gym.name}-${gym.city}`}
            onClick={() => onGymClick && onGymClick(gym.state)}
            className="flex items-start gap-3 bg-zinc-900/80 border border-zinc-800 p-3 hover:border-[#e8ff00]/30 transition-all text-left group"
            data-testid={`top-rated-gym-${i}`}
          >
            {/* Rank badge */}
            <div className={`flex-shrink-0 w-7 h-7 flex items-center justify-center font-heading text-xs ${
              RANK_BADGE_CLASSES[i] || RANK_BADGE_FALLBACK
            }`}>
              {i + 1}
            </div>

            {/* Gym info */}
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-heading leading-tight truncate group-hover:text-[#e8ff00] transition-colors">
                {gym.name}
              </p>
              <p className="text-zinc-500 text-[11px] flex items-center gap-1 mt-0.5">
                <MapPin className="w-2.5 h-2.5 flex-shrink-0" /> {gym.city}, {gym.state}
              </p>
              {gym.latestReview && (
                <p className="text-zinc-600 text-[10px] italic mt-1 truncate">
                  "{gym.latestReview.text}"
                </p>
              )}
            </div>

            {/* Rating */}
            <div className="flex-shrink-0 text-right">
              <div className="flex items-center gap-1">
                <Star className="w-3 h-3 fill-[#e8ff00] text-[#e8ff00]" />
                <span className="text-white text-sm font-heading">{gym.avg}</span>
              </div>
              <p className="text-zinc-600 text-[10px]">{gym.count} review{gym.count !== 1 ? 's' : ''}</p>
            </div>
          </button>
        ))}
      </div>

      {reviews.length > 0 && (
        <p className="text-zinc-600 text-[10px] text-center mt-3">
          Rankings based on user-submitted reviews. <span className="text-zinc-500">Rate your gym to influence the leaderboard.</span>
        </p>
      )}
    </div>
  );
};

export default TopRatedGyms;
